/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Fragment, useState, type FunctionComponent } from 'react'
import { useLocation } from 'react-router-dom'

import { EuiTitle, EuiFlexGroup, EuiFlexItem, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import LandingPageOuterContainer from '@modules/access-management-components/LandingPageOuterContainer'
import LandingPageInnerContainer from '@modules/access-management-components/LandingPageInnerContainer'
import LandingPageSeparator from '@modules/access-management-components/HorizontalSeparator'
import ContactSupport from '@modules/access-management-components/ContactSupport'
import { isCCMFeatureActivated } from '@modules/ccm-user-lib/feature'

import MarketplaceSignUp from './MarketplaceSignUp'
import UserRegistrationForm from './Form'

import type { LandingPageContext } from '../LandingPageContainer/types'

interface Props {
  createUser: ({ email, password, captcha_token }) => Promise<any>
  isServerlessUserRegistration?: boolean
  isMarketplaceSignupEnabled?: boolean
  redirectTo?: string
}

const getContentAndSettings = ({
  isSwitchingSso,
  isCCM,
}: {
  isSwitchingSso: boolean
  isCCM: boolean
}) => {
  if (isSwitchingSso) {
    return {
      panelCssOverride: css({ maxWidth: '500px', alignSelf: 'center' }),
      v1LandingPageInnerContainerPanelMaxWidth: '500px',
      footer: <ContactSupport />,
    }
  }

  return {
    v1LandingPageInnerContainerPanelMaxWidth: 'none',
    title: isCCM ? (
      <FormattedMessage
        id='signup-cloud-wrapper.title.elastic-cloud'
        defaultMessage='Sign up for Elastic Cloud'
      />
    ) : (
      <FormattedMessage
        id='signup-cloud-wrapper.title.free-trial'
        defaultMessage='Sign up for a free trial'
      />
    ),
    subtitle: (
      <FormattedMessage
        id='signup-cloud-wrapper.subtitle'
        defaultMessage='No credit card required'
      />
    ),
    pageContext: { name: 'signup' } as LandingPageContext,
  }
}

const SignUpPage: FunctionComponent<Props> = ({
  createUser,
  isMarketplaceSignupEnabled,
  redirectTo,
}: Props) => {
  const [isSwitchingToSso, setIsSwitchingToSso] = useState<boolean>(false)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const onboardingServiceType = searchParams.get('onboarding_service_type')
  const isCCMRegistration = isCCMFeatureActivated() && onboardingServiceType === 'ccm'

  const showMarketplaceSignUp =
    isMarketplaceSignupEnabled && !isSwitchingToSso && !isCCMRegistration

  const { title, pageContext, panelCssOverride, footer } = getContentAndSettings({
    isSwitchingSso: !!isSwitchingToSso,
    isCCM: isCCMFeatureActivated(),
  })

  return (
    <LandingPageOuterContainer pageContext={pageContext} footer={footer}>
      <EuiFlexItem grow={false} css={{ alignItems: 'center' }}>
        <EuiFlexGroup alignItems='center' direction='column' gutterSize='m'>
          <EuiFlexItem>
            <LandingPageInnerContainer title={title} panelCssOverride={panelCssOverride}>
              <UserRegistrationForm
                createUser={createUser}
                redirectTo={redirectTo}
                isSwitchingToSso={isSwitchingToSso}
                setIsSwitchingToSso={setIsSwitchingToSso}
              />

              {showMarketplaceSignUp && (
                <Fragment>
                  <EuiSpacer size='l' />

                  <LandingPageSeparator
                    label={
                      <EuiTitle size='m'>
                        <FormattedMessage id='signup-page.separator' defaultMessage='OR' />
                      </EuiTitle>
                    }
                  />
                </Fragment>
              )}
            </LandingPageInnerContainer>
          </EuiFlexItem>
          {showMarketplaceSignUp && <MarketplaceSignUp />}
        </EuiFlexGroup>
      </EuiFlexItem>
    </LandingPageOuterContainer>
  )
}

export default SignUpPage
